import { Component, Element, Host, h, Prop, State, Watch } from "@stencil/core";
import { HeaderMenuItemType } from "../interface";
import { findMenuItemElements, getMenuItemHasSlot } from "../utils";

const baseClass = "uic-header-menu-item";

/**
 * @slot (default) - Label of the menu item. If not provided, the `label` attribute must be set (accessibility).
 */
@Component({
    tag: "uic-header-menu-item",
    styleUrl: "header-menu-item.scss",
    shadow: false,
})
export class HeaderMenuItem {
    @Element() host: HTMLUicHeaderMenuItemElement;

    @State() hasSlot: boolean;

    /** Name that must be unique in the menu scope identifies the menu-item */
    @Prop({ reflect: true }) name: string;

    /** Flag that indicates if the menu item is disabled */
    @Prop({ reflect: true }) disabled = false;

    /** The level in a nested menu */
    @Prop({ reflect: true }) level: number = 0;

    /** Flag that indicates if the menu item is currently selected or not */
    @Prop({ reflect: true }) selected = false;

    /** Label text use as aria-label attribute and slot fallback */
    @Prop() label?: string;

    /** Optional badge text */
    @Prop() badge?: string | number;

    /** If set, the menu item acts as a link */
    @Prop() href?: string;

    /** Used for the link target attribute if `href` is set */
    @Prop() target?: string;

    /** Used for the link download attribute if `href` is set */
    @Prop() download?: string;

    /** Name of the icon that should be rendered instead of or before the label */
    @Prop() icon?: string;

    /** Flag that indicates that the menu-item only renders an icon without label */
    @Prop() iconOnly?: boolean;

    /** Name of the icon that should be rendered after the label */
    @Prop() trailingIcon?: string;

    /** Data provider for the menu child elements */
    @Prop() items: HeaderMenuItemType[] | null = null;

    /** The parent item element in a nested menu */
    @Prop() parent: HTMLUicHeaderMenuItemElement | null = null;

    /** If the item has nested items */
    @Prop() expanded: boolean = false;

    /** Update menu items */
    @Watch("expanded")
    setExpanded() {
        const { expanded, parent, level } = this;
        if (!expanded) {
            findMenuItemElements(this.host).forEach((el) => {
                el.expanded = false;
            });
            return;
        }
        if (parent === null) {
            return;
        }
        findMenuItemElements(parent, `[level="${level}"]`).forEach((el) => {
            if (el !== this.host) {
                el.expanded = false;
            }
        });
    }

    private renderItems() {
        const { items, level } = this;
        if (!Array.isArray(items) || items.length === 0) {
            return null;
        }
        return items.map((item) => (
            <uic-header-menu-item
                key={item.name}
                parent={this.host}
                level={level + 1}
                name={item.name}
                icon={item.icon}
                label={item.label}
                items={item.items}
                href={item.href}
                target={item.target}
                download={item.download}
            />
        ));
    }

    componentDidLoad() {
        this.hasSlot = getMenuItemHasSlot(this.host);
    }

    render() {
        const {
            badge,
            disabled,
            expanded,
            icon,
            iconOnly,
            items,
            label,
            level,
            selected,
        } = this;
        const badgeText = badge !== undefined ? `${badge}` : "";
        const hasChildren = Array.isArray(items) && items.length > 0;
        const expandIcon = level > 0 ? "uic:chevron-right" : "uic:chevron-down";
        const trailingIcon = hasChildren
            ? this.trailingIcon || expandIcon
            : this.trailingIcon;
        const isLink = typeof this.href === "string" && this.href.length > 0;
        const ItemTag = isLink ? "a" : "div";
        return (
            <Host
                class={{
                    [`${baseClass}`]: true,
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--selected`]: selected,
                    [`${baseClass}--icon-only`]: iconOnly,
                }}
                aria-disabled={disabled}
                aria-selected={selected}
                aria-label={label}
                tabindex="-1"
            >
                <ItemTag
                    class={`${baseClass}__item`}
                    href={this.href}
                    target={isLink ? this.target : undefined}
                    download={isLink ? this.download : undefined}
                >
                    {icon && <uic-icon name={icon} />}
                    <span class={`${baseClass}__label`}>
                        <span class={`${baseClass}__label-slot`}>
                            <slot />
                        </span>
                        {label && !this.hasSlot && (
                            <span class={`${baseClass}__label-text`}>
                                {label}
                            </span>
                        )}
                    </span>
                    {trailingIcon && <uic-icon name={trailingIcon} />}
                    {badgeText.length > 0 && <uic-badge value={badgeText} />}
                    <slot name="notifications-bell"></slot>
                </ItemTag>
                {hasChildren && (
                    <div
                        class={{
                            [`${baseClass}__submenu`]: true,
                            [`${baseClass}__submenu--vertical`]: level > 0,
                            [`${baseClass}__submenu--expanded`]: expanded,
                        }}
                    >
                        {this.renderItems()}
                    </div>
                )}
            </Host>
        );
    }
}
